import type { TagCategory } from "@/lib/tag-taxonomy-store"

export type BooruTagCategory = "artist" | "character" | "copyright" | "general" | "meta"
export type VisiSearchTagCategory = "characters" | "artists" | "series" | "general"

export interface NormalizedTagSuggestion {
  name: string
  category: BooruTagCategory
  postCount?: number
  /** The retired name this tag replaces, when the search was for an old alias (Danbooru: bare_feet -> barefoot) */
  antecedent?: string
  source: string
  rawType?: string | number | null
}

export type RawTagSuggestion = {
  name?: string
  tag?: string
  value?: string
  label?: string
  title?: string
  category?: string | number | null
  type?: string | number | null
  rawType?: string | number | null
  post_count?: string | number | null
  antecedent?: string | null
  postCount?: string | number | null
  count?: string | number | null
  posts?: string | number | null
  frequency?: string | number | null
}

export function normalizeTagName(value: unknown): string {
  if (typeof value === "number") return String(value)
  return typeof value === "string" ? value.trim() : ""
}

export function normalizeSuggestionKey(tag: string): string {
  return tag.trim().toLowerCase().replace(/\s+/g, "_")
}

export function normalizePostCount(value: unknown): number | undefined {
  const count = typeof value === "number" ? value : Number.parseInt(String(value || ""), 10)
  return Number.isFinite(count) ? count : undefined
}

export function mapRawTagCategory(type: unknown): BooruTagCategory {
  const normalized = String(type ?? "").toLowerCase()

  if (normalized === "1" || normalized.includes("artist")) return "artist"
  if (normalized === "3" || normalized.includes("copyright") || normalized.includes("series")) return "copyright"
  if (normalized === "4" || normalized.includes("character")) return "character"
  if (normalized === "5" || normalized.includes("meta")) return "meta"

  return "general"
}

export function toTaxonomyCategory(category: BooruTagCategory): TagCategory {
  if (category === "artist") return "artist"
  if (category === "character") return "character"
  if (category === "copyright") return "copyright"
  if (category === "meta") return "meta"
  return "general"
}

export function toVisiSearchCategory(category: BooruTagCategory): VisiSearchTagCategory {
  if (category === "artist") return "artists"
  if (category === "character") return "characters"
  if (category === "copyright") return "series"
  return "general"
}

export function uniqueTagSuggestions(tags: NormalizedTagSuggestion[], limit: number): NormalizedTagSuggestion[] {
  const seen = new Set<string>()
  const result: NormalizedTagSuggestion[] = []

  for (const tag of tags) {
    const key = normalizeSuggestionKey(tag.name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    result.push({ ...tag, name: tag.name.trim() })
    if (result.length >= limit) break
  }

  return result
}

export function normalizeRawTagSuggestion(item: RawTagSuggestion | string, source: string): NormalizedTagSuggestion | null {
  if (typeof item === "string") {
    const name = normalizeTagName(item)
    return name ? { name, category: "general", source, rawType: null } : null
  }

  // Take the first candidate that is actually a non-empty string. `||` alone
  // picked Danbooru's `tag`, which is an object ({ id, name, post_count, ... }),
  // over its `value` string, so normalizeTagName returned "" and every Danbooru
  // suggestion was dropped even when the request succeeded.
  const candidates: unknown[] = [item.name, item.tag, item.value, item.label, item.title]
  const name = candidates.map(normalizeTagName).find((n) => n.length > 0) ?? ""
  if (!name) return null

  const rawType = item.rawType ?? item.category ?? item.type ?? null
  return {
    name,
    category: mapRawTagCategory(rawType),
    postCount: normalizePostCount(item.postCount ?? item.post_count ?? item.count ?? item.posts ?? item.frequency),
    ...(typeof item.antecedent === "string" && item.antecedent.trim() ? { antecedent: item.antecedent.trim() } : {}),
    source,
    rawType,
  }
}

export function normalizeRawTagSuggestions(items: unknown, source: string, limit: number): NormalizedTagSuggestion[] {
  if (!Array.isArray(items)) return []
  const tags = items
    .map((item) => normalizeRawTagSuggestion(item as RawTagSuggestion | string, source))
    .filter((tag): tag is NormalizedTagSuggestion => !!tag)

  return uniqueTagSuggestions(tags, limit)
}

/** The old name behind each suggestion that is an alias (keyed like the category map), for sites that report one. */
export function buildAntecedentMap(items?: Array<Partial<NormalizedTagSuggestion> & RawTagSuggestion>): Record<string, string> {
  const map: Record<string, string> = {}
  if (!Array.isArray(items)) return map

  for (const item of items) {
    const name = normalizeTagName(item.name || item.tag || item.value || item.label || item.title)
    const old = typeof item.antecedent === "string" ? item.antecedent.trim() : ""
    if (name && old) map[normalizeSuggestionKey(name)] = old
  }

  return map
}

/** Post count per suggested tag (keyed like the category map), for sites that report one. */
export function buildPostCountMap(items?: Array<Partial<NormalizedTagSuggestion> & RawTagSuggestion>): Record<string, number> {
  const counts: Record<string, number> = {}
  if (!Array.isArray(items)) return counts

  for (const item of items) {
    const name = normalizeTagName(item.name || item.tag || item.value || item.label || item.title)
    const count = normalizePostCount(item.postCount ?? item.post_count ?? item.count)
    if (name && count !== undefined) counts[normalizeSuggestionKey(name)] = count
  }

  return counts
}

export function buildTaxonomyCategoryMap(items?: Array<Partial<NormalizedTagSuggestion> & RawTagSuggestion>): Record<string, TagCategory> {
  const categoryMap: Record<string, TagCategory> = {}
  if (!Array.isArray(items)) return categoryMap

  for (const item of items) {
    const name = normalizeTagName(item.name || item.tag || item.value || item.label || item.title)
    if (!name) continue
    const category = item.category && ["artist", "character", "copyright", "general", "meta"].includes(String(item.category))
      ? item.category as BooruTagCategory
      : mapRawTagCategory(item.rawType ?? item.type ?? item.category)
    categoryMap[normalizeSuggestionKey(name)] = toTaxonomyCategory(category)
  }

  return categoryMap
}

export function parseGelbooruStyleTagXml(xml: string, source: string, limit: number): NormalizedTagSuggestion[] {
  const tags: NormalizedTagSuggestion[] = []
  const tagRegex = /<tag\b([^>]*)\/?>/gi
  const attrRegex = /([a-zA-Z_:-]+)="([^"]*)"/g
  let match: RegExpExecArray | null

  while ((match = tagRegex.exec(xml)) !== null) {
    const attrs: Record<string, string> = {}
    let attrMatch: RegExpExecArray | null
    while ((attrMatch = attrRegex.exec(match[1])) !== null) {
      attrs[attrMatch[1]] = attrMatch[2]
    }

    const normalized = normalizeRawTagSuggestion({
      name: attrs.name,
      type: attrs.type,
      count: attrs.count,
    }, source)
    if (normalized) tags.push(normalized)
  }

  return uniqueTagSuggestions(tags, limit)
}
